import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Mui, MuiIcons } from '@leap4/frontend';
import { isOnboardingGreetingUser } from '../../App/partyMode';
import {
    IOnboardingImportResult,
    IOnboardingIssue,
    IOnboardingPreview,
    IOnboardingPublisherPlan,
    OnboardingResolutions,
} from './appAdsLines.interface';
import {
    downloadOnboardingIssues,
    downloadOnboardingTemplate,
    errorMessage,
    importOnboardingSheet,
    previewOnboardingSheet,
    rejectionFromError,
} from './appAdsLines.api';
import { readOnboardingSheet } from './readSheet';

const {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fade,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} = Mui;
const { Download: DownloadIcon, UploadFile: UploadFileIcon } = MuiIcons;

/**
 * A greeting for exactly one person, in the spirit of party mode: it replaces the modal's
 * instructions for a few seconds, fades out, and the real text takes its place. Nothing
 * depends on it and nobody else ever sees it.
 */
const GREETING = `This is where you upload a file to seed publishers and lines. Just one file. After you upload it, you check it. Checking is important, because it tells you whether the file is good, and a good file is the foundation of a good import. Once you've checked it, you can import it. Or not. Importing is entirely optional, and nobody here will judge you either way. Probably.

Now, before you go: if you ever have any complaints about PAPA2, whatsoever, big or small, please address the dev team. The dev team loves listening to complaints. The dev team is happy to listen to your complaints specifically, reflect on those complaints, and try to fix whatever ails you.

Trust in the dev team. We have heard your feedback about wanting less text in the modals, and have improved the situation. We are watching. We are legion.`;

/** How long it stays up, and how long it takes to go. */
const GREETING_MS = 10000;
const GREETING_FADE_MS = 2000;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const connector = connect((state: any) => ({ authEmail: state.auth?.user?.email }));
type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
    open: boolean;
    onClose: () => void;
    onImported: (result: IOnboardingImportResult) => void;
}

type Props = OwnProps & PropsFromRedux;

/**
 * Onboarding bulk upload: one sheet that brings a new client's publishers and their lines in
 * together, so nobody has to create eleven publishers by hand and then import lines into each.
 *
 * Three things shape the flow, all of them decisions rather than defaults:
 *
 *  - **Preview always, commit second.** The preview writes nothing and is where every row
 *    problem and every publisher disagreement is shown.
 *  - **Blocking versus advisory.** A row we can't read stops the whole file, because importing
 *    the readable ones would leave someone believing their list is complete. Duplicates and
 *    already-removed lines are reported and the import proceeds.
 *  - **Keep beats take.** Where the sheet disagrees with PAPA2 about a publisher, PAPA2 wins
 *    unless someone says otherwise here. A sheet is often the stale copy.
 */
const _OnboardingUploadModal: React.FC<Props> = ({ open, onClose, onImported, authEmail }) => {
    const [fileName, setFileName] = useState('');
    const [rows, setRows] = useState<string[][] | null>(null);
    const [preview, setPreview] = useState<IOnboardingPreview | null>(null);
    const [resolutions, setResolutions] = useState<OnboardingResolutions>({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Two flags rather than one: `showing` drives the fade, `done` swaps in the real text only
    // once the fade has finished, so the two never overlap and the dialog doesn't jump.
    const greetingUser = isOnboardingGreetingUser(authEmail);
    const [greetingShowing, setGreetingShowing] = useState(false);
    const [greetingDone, setGreetingDone] = useState(true);

    useEffect(() => {
        if (!open || !greetingUser) {
            setGreetingShowing(false);
            setGreetingDone(true);
            return undefined;
        }
        setGreetingShowing(true);
        setGreetingDone(false);
        const timer = setTimeout(() => setGreetingShowing(false), GREETING_MS);
        return () => clearTimeout(timer);
    }, [open, greetingUser]);

    const reset = () => {
        setFileName('');
        setRows(null);
        setPreview(null);
        setResolutions({});
        setError('');
    };

    const close = () => {
        reset();
        onClose();
    };

    const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Cleared so picking the same file twice after a failure still fires onChange.
        e.target.value = '';
        if (!file) return;

        reset();
        setFileName(file.name);
        setBusy(true);
        try {
            // Rejects a workbook with more than one sheet before anything reaches the server.
            const parsed = await readOnboardingSheet(file);
            setRows(parsed);
            setPreview(await previewOnboardingSheet(parsed));
        } catch (err: any) {
            setRows(null);
            setError(err?.message || errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const setResolution = (domain: string, field: string, value: 'keep' | 'take') =>
        setResolutions((prev) => ({ ...prev, [domain]: { ...(prev[domain] || {}), [field]: value } }));

    /** Applies one choice to every field of a publisher — the common case is all-or-nothing. */
    const setAllForPublisher = (plan: IOnboardingPublisherPlan, value: 'keep' | 'take') =>
        setResolutions((prev) => ({
            ...prev,
            [plan.domain]: plan.fields.reduce((acc, f) => ({ ...acc, [f.field]: value }), {}),
        }));

    const resolutionFor = (domain: string, field: string, fallback: 'keep' | 'take') =>
        resolutions[domain]?.[field] || fallback;

    const conflicted = useMemo(
        () => (preview?.publishers || []).filter((p) => p.fields.length > 0),
        [preview],
    );

    const blocked = !!preview?.blocking?.length;
    const nothingToDo =
        !!preview && !blocked && preview.totals.linesToAdd === 0 && preview.totals.publishersToCreate === 0;

    const submit = async () => {
        if (!rows || blocked) return;
        setBusy(true);
        setError('');
        try {
            onImported(await importOnboardingSheet(rows, resolutions));
            close();
        } catch (err: any) {
            const rejection = rejectionFromError(err);
            if (rejection) {
                setPreview((prev) => (prev ? { ...prev, blocking: rejection.issues as IOnboardingIssue[] } : prev));
                setError(rejection.message);
            } else {
                setError(errorMessage(err));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="md">
            <DialogTitle>Upload publishers and lines</DialogTitle>
            <DialogContent dividers>
                {!greetingDone ? (
                    <Fade
                        in={greetingShowing}
                        timeout={{ enter: 0, exit: GREETING_FADE_MS }}
                        onExited={() => setGreetingDone(true)}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                            sx={{ whiteSpace: 'pre-line' }}
                        >
                            {GREETING}
                        </Typography>
                    </Fade>
                ) : (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        One sheet with a row per line:{' '}
                        <strong>
                            Publisher Name, Domain, app-ads.txt, Seller ID, Account manager, Line, Partner
                        </strong>
                        .
                    </Typography>
                )}

                <Box display="flex" gap={1} alignItems="center" my={2} flexWrap="wrap">
                    <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
                        Choose file
                        <input type="file" accept=".csv, .xlsx, .xls" hidden onChange={onFile} />
                    </Button>
                    <Button size="small" startIcon={<DownloadIcon />} onClick={downloadOnboardingTemplate}>
                        Download template
                    </Button>
                    {!!fileName && (
                        <Typography variant="body2" color="text.secondary">
                            {fileName}
                        </Typography>
                    )}
                </Box>

                {busy && <LinearProgress sx={{ mb: 2 }} />}
                {!!error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {preview && (
                    <>
                        <Alert
                            severity={blocked ? 'error' : nothingToDo ? 'info' : 'success'}
                            // The message column is what holds the text; without flexGrow it
                            // shrinks to its content and centring inside it does nothing.
                            sx={{ mb: 2, '& .MuiAlert-message': { flexGrow: 1, textAlign: 'center' } }}
                        >
                            <AlertTitle>
                                {blocked
                                    ? `${preview.blocking.length} of ${preview.rowsRead} rows can't be read`
                                    : `Read ${plural(preview.rowsRead, 'row')}`}
                            </AlertTitle>
                            {blocked ? (
                                'Nothing will be imported until these are fixed. The row numbers match your file.'
                            ) : nothingToDo ? (
                                'Everything in this file is already in PAPA2 — importing would change nothing.'
                            ) : (
                                <>
                                    {plural(preview.totals.publishersToCreate, 'new publisher')},{' '}
                                    {preview.totals.publishersExisting} already tracked,{' '}
                                    {plural(preview.totals.linesToAdd, 'line')} to add
                                    {preview.totals.linesAlreadyPresent
                                        ? `, ${preview.totals.linesAlreadyPresent} already there`
                                        : ''}
                                    {preview.totals.linesRemoved
                                        ? `, ${preview.totals.linesRemoved} skipped as removed`
                                        : ''}
                                    .
                                </>
                            )}
                        </Alert>

                        <IssueList
                            title="Must be fixed"
                            severity="error"
                            issues={preview.blocking}
                            emptyLabel=""
                        />
                        <IssueList
                            title="Warnings — the import still runs"
                            severity="warning"
                            issues={preview.advisory}
                            emptyLabel=""
                        />

                        {(!!preview.blocking.length || !!preview.advisory.length) && (
                            <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                sx={{ mb: 2 }}
                                onClick={() => downloadOnboardingIssues(preview.blocking, preview.advisory)}
                            >
                                Download all issues (CSV)
                            </Button>
                        )}

                        {!!conflicted.length && (
                            <Box mb={2}>
                                <Typography variant="subtitle2" gutterBottom>
                                    These publishers already exist and the sheet disagrees
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                    PAPA2's value is kept unless you choose otherwise.
                                </Typography>
                                {conflicted.map((plan) => (
                                    <Box key={plan.domain} border={1} borderColor="divider" borderRadius={1} p={1.5} mb={1}>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                                            <Typography variant="body2" fontWeight={500}>
                                                {plan.name}
                                            </Typography>
                                            <Chip size="small" variant="outlined" label={plan.domain} />
                                            <Box flexGrow={1} />
                                            <Button size="small" onClick={() => setAllForPublisher(plan, 'keep')}>
                                                Keep all
                                            </Button>
                                            <Button size="small" onClick={() => setAllForPublisher(plan, 'take')}>
                                                Take all from file
                                            </Button>
                                        </Box>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Field</TableCell>
                                                    <TableCell>In PAPA2</TableCell>
                                                    <TableCell>In file</TableCell>
                                                    <TableCell align="right">Use</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {plan.fields.map((f) => {
                                                    const value = resolutionFor(plan.domain, f.field, f.resolution);
                                                    return (
                                                        <TableRow key={f.field}>
                                                            <TableCell>{FIELD_LABELS[f.field] || f.field}</TableCell>
                                                            <TableCell
                                                                sx={{ opacity: value === 'keep' ? 1 : 0.5 }}
                                                            >
                                                                {f.inPapa || <em>empty</em>}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{ opacity: value === 'take' ? 1 : 0.5 }}
                                                            >
                                                                {f.inFile}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <ToggleButtonGroup
                                                                    size="small"
                                                                    exclusive
                                                                    value={value}
                                                                    onChange={(_e, v) =>
                                                                        v && setResolution(plan.domain, f.field, v)
                                                                    }
                                                                >
                                                                    <ToggleButton value="keep">PAPA2</ToggleButton>
                                                                    <ToggleButton value="take">File</ToggleButton>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {!!preview.publishers.length && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" align="center" gutterBottom>
                                    What will be uploaded
                                </Typography>
                                {/* Every publisher the file touches, conflicts included - this is
                                    the prediction, not a leftovers list. The block above is where
                                    conflicts get decided; this is what lands once they are. */}
                                <Box display="flex" flexDirection="column" gap={0.25} alignItems="center">
                                    {preview.publishers.map((plan) => (
                                        <Typography key={plan.domain} variant="caption" color="text.secondary">
                                            {plan.name} ({plan.domain}) — {plan.isNew ? 'new publisher, ' : ''}
                                            {plan.linesToAdd
                                                ? `${plural(plan.linesToAdd, 'line')} added`
                                                : 'no new lines'}
                                            {plan.linesAlreadyPresent
                                                ? `, ${plan.linesAlreadyPresent} already tracked`
                                                : ''}
                                            {plan.linesRemoved
                                                ? `, ${plan.linesRemoved} skipped as removed`
                                                : ''}
                                        </Typography>
                                    ))}
                                </Box>
                            </>
                        )}

                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={submit}
                    disabled={busy || !preview || blocked || nothingToDo}
                >
                    Import
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const OnboardingUploadModal = connector(_OnboardingUploadModal);

const FIELD_LABELS: Record<string, string> = {
    name: 'Publisher name',
    appAdsTxtUrl: 'app-ads.txt URL',
    sellerId: 'Seller ID',
    accountManagers: 'Account managers',
};

/**
 * Every issue, not a truncated sample.
 *
 * The lines importers cap this at twelve, which is right for a paste of a few rows; here the
 * file is a few hundred and a cap means fixing twelve, re-uploading, and finding twelve more.
 * The list scrolls instead, and the CSV download exists for working through it offline.
 */
const IssueList: React.FC<{
    title: string;
    severity: 'error' | 'warning';
    issues: IOnboardingIssue[];
    emptyLabel?: string;
}> = ({ title, severity, issues, emptyLabel }) => {
    if (!issues?.length) return emptyLabel ? <Typography variant="caption">{emptyLabel}</Typography> : null;

    return (
        <Alert severity={severity} sx={{ mb: 2 }}>
            <AlertTitle>
                {title} ({issues.length})
            </AlertTitle>
            <Box sx={{ maxHeight: 220, overflowY: 'auto', fontSize: 13 }}>
                {issues.map((issue, i) => (
                    <Box key={`${issue.row}-${i}`} mb={0.5}>
                        <strong>Row {issue.row}</strong>
                        {issue.publisher ? ` · ${issue.publisher}` : ''} — {issue.reason}
                        {issue.text ? (
                            <Box sx={{ fontFamily: 'monospace', opacity: 0.75, wordBreak: 'break-all' }}>
                                {issue.text}
                            </Box>
                        ) : null}
                    </Box>
                ))}
            </Box>
        </Alert>
    );
};
